import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { Amcharts } from './Amcharts';

export class AmchartsColumnchart extends Amcharts {
    constructor() {
        super();

        this.whenReady((_ev: any) => {
            this.chart.events.on("datavalidated", (_e: any) => {
                this.chart.yAxes.each((valueAxis: any) => {
                    valueAxis.zoomable = false;
                    valueAxis.toggleZoomOutButton = false;
                });
            });
        });
    }

    createByRef(ref: any, context: any): any {
        this.create(ref, am4charts.XYChart, context);
        this.applyTheme(context);
        this.removeSerieses();
        this.createAxis(context);
        this.updateValueAxes(context);
        for (let j = 0; j < context.dimensions.length; j++) {
            let dimension = context.dimensions[j];
            let dimensionResultName = dimension.resultName || "key" + j;
            this.createCategoryAxis(dimensionResultName, context);
        }
        this.updateCategoryAxis(context);

        this.createSerieses(context);
        this.updateMeasures(context);
        this.updateDimensions(context);

        if (this.shouldUseCursor(context)) {
            let cursor = new am4charts.XYCursor();
            cursor.behavior = "panXY";
            this.chart.cursor = cursor;
        }

        //this._applyScrollbar(context.settings?.labelMaxWidth);

        return this;
    }

    //recreate(ref: any, context: any) {
    //    this.dispose();
    //    this.createByRef(ref, context);
    //}

    createAxis(context: any) {
        let doGlobalAxisExist = false;
        let doGlobalDeltaAxisExist = false;
        let measures = this.context.isCluster ? context.basicMeasures : context.measures;

        measures?.forEach((measure: any, i: any) => {
            let measureResultName = measure.resultName || "value" + i;
            let measureAppearance = context.settings.measures?.length
                ? context.settings.measures?.find((m: any) => m.name === measureResultName)
                : {};

            let measureContext = {
                measure,
                measureAppearance,
                settings: context.settings,
            }

            let isDeltaPercent = measure?.valueKind == 2;

            if (isDeltaPercent) {
                if (measureAppearance?.hasOwnAxis || !doGlobalDeltaAxisExist) {
                    this.createDeltaValueAxis(measureContext);
                    doGlobalDeltaAxisExist = true;
                }
            }
            else if (measureAppearance?.hasOwnAxis || !doGlobalAxisExist) {
                this.createValueAxis(measureContext);
                doGlobalAxisExist = true;
            }
        });
    }

    createValueAxis(context: any) {
        let measure = context?.measure;
        let measureAppearance = context?.measureAppearance;
        let settings = context?.settings;

        let valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.dataFields.name = measure?.name ?? '';
        valueAxis.dataFields.resultName = measure?.resultName ?? "value0";
        valueAxis.numberFormatter = new am4core.NumberFormatter();
        valueAxis.max = settings.axisMaxValue;
        valueAxis.min = settings.axisMinValue;
        if (context.settings.axisGridDistance != null)
            valueAxis.renderer.minGridDistance = context.settings.axisGridDistance;

        if (context.settings?.stacked && context.settings?.percentage)
            valueAxis = this.setPercentageAxis(valueAxis);
        else {
            valueAxis.numberFormatter.numberFormat = this.getMeasureNumberFormat(measureAppearance, settings);
            valueAxis.numberFormatter.bigNumberPrefixes = this.getBigNumberPrefixes(measureAppearance, settings);
        }

        valueAxis.showOnInit = true;
        valueAxis.keepSelection = true;

        this.showValueAxisTooltip(valueAxis, context.settings.showValueAxisTooltip);
        this.setFixedBigNumberValueAxis(valueAxis, measureAppearance, settings);
        this.setLabelVisibility(valueAxis, settings);
        this.setCustomLabelsIfNeeded(valueAxis, measureAppearance);

        return valueAxis;
    }

    createDeltaValueAxis(context: any) {
        let valueAxis = this.createValueAxis(context);

        valueAxis.dataFields.deltaName = "_delta_" + context.measure.resultName;
        valueAxis.dataFields.hasDelta = true;

        this.showValueAxisTooltip(valueAxis, context.settings.showValueAxisTooltip);

        if (valueAxis.numberFormatter.numberFormat.includes('a'))
            valueAxis.numberFormatter.numberFormat = "#,###.";

        return valueAxis;
    }

    createCategoryAxis(categoryFieldName: any, context: any) {
        if (this.chart.xAxes.length) {
            this.updateCategoryAxis(context);
            return this.chart.xAxes.getIndex(0);
        }
        else {
            let categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
            categoryAxis.dataFields.category = categoryFieldName;
            categoryAxis.tooltip.label.maxWidth = 200;
            categoryAxis.tooltip.label.truncate = true;
            categoryAxis.cursorTooltipEnabled = false;
            categoryAxis.renderer.labels.template.adapter.add("text", (label: any, target: any) => {
                if (label)
                    label = label?.replace('{', '')?.replace('}', '');

                return this.dateCategoryFormatter(label);
            });

            this.updateCategoryAxisSettings(categoryAxis, context);
            return categoryAxis;
        }
    }

    createSerieses(context: any) {
        let measures = context.measures;
        let dimensions = context.dimensions;

        for (let i = 0; i < measures.length; i++) {
            let measure = measures[i];
            let valueFieldName = measure.resultName || "value" + i;
            let isDelta = measure?.valueKind != 0;

            for (let j = 0; j < dimensions.length; j++) {
                let dimension = dimensions[j];
                let dimensionResultName = dimension.resultName || "key" + j;
                this.createSeries(valueFieldName, dimensionResultName, context, isDelta, i);
            }
        }
    }

    createSeries(valueFieldName: any, categoryFieldName: any, context: any, isDelta: any, measureIndex: any, keyDimensionName: any = null, seriesTitle: any = null) {
        const definedFieldName = isDelta ? "_delta_" + valueFieldName : valueFieldName;
        const { settings, measures, dimensions, isCluster } = context;
        const data = this.chart.data;
        const seriesType = this.getSeriesType(valueFieldName, settings);
        const measure = measures.find((f: any) => f.resultName == valueFieldName);
        const measureAppearance = isCluster ? settings.measures[0] : settings.measures.find((f: any) => f.name == valueFieldName);
        const bigPrefixes = this.getBigNumberPrefixes(measureAppearance, settings);
        const basicMeasures = context.basicMeasures ?? measures;
        const dimensionAppearance = settings?.dimensions?.find((d: any) => categoryFieldName.includes(d.name));
        const additional = { dimensionAppearance, valueFieldName, definedFieldName, categoryFieldName, keyDimensionName, seriesTitle }

        const blendSetting = context.blendSettings?.find((b: any) => {
            return b.measures.find((m: any) => {
                return m.name === valueFieldName;
            });
        });

        let numberFormat = this.getMeasureNumberFormat(measureAppearance, blendSetting ?? settings);
        numberFormat += this.percentSignIfNeeded(numberFormat, measure, isDelta, basicMeasures, isCluster, measureIndex);

        let series = this.createSeriesByType(seriesType, measure, numberFormat, bigPrefixes, blendSetting ?? settings, valueFieldName);

        const dimension = dimensions.find((d: any) => d.resultName == categoryFieldName);
        //const dimensionIsDateTime = dimension?.dataType == 3;
        const category = /*dimensionIsDateTime ? "dateX" :*/ "categoryX";
        const valueFormat = settings.stacked && settings.percentage ? "{valueY.totalPercent.formatNumber(#'" + (numberFormat.match(/\.0+/)?.[0] ?? ".") + "')}%" : "{valueY.formatNumber('" + numberFormat + "')}";

        if (series instanceof am4charts.ColumnSeries)
            this.setSeriesColumnProperties(series, blendSetting ?? settings, category, valueFormat, measureAppearance, context, numberFormat, data, additional);
        else
            this.setSeriesLineProperties(series, blendSetting ?? settings, category, valueFormat, measureAppearance, context, data, additional);

        if (measureAppearance?.bulletsType === "last")
            this.setBulletsVisibility(data, measureAppearance, additional);

        this.setFixedBigNumberTooltip(series, measureAppearance, blendSetting ?? settings);
        //if (dimensionIsDateTime)
        //    series.dataFields.dateX = categoryFieldName;
        //else
        series.dataFields.categoryX = categoryFieldName;

        series.dataFields.valueY = definedFieldName;

        let hasOwnAxis = measureAppearance?.hasOwnAxis;

        this.appendThreshold(context, series, valueFieldName, categoryFieldName, series.yAxis);

        this.updateSeries(context, valueFieldName);
        series.yAxis = this.getAppropriateYAxis(definedFieldName, context, hasOwnAxis, isDelta);

        this.setSeriesSettings(series, blendSetting ?? settings, valueFormat, context, measureAppearance, measureIndex);

        this.applySeriesTheme(series, context, measureAppearance, measureIndex);

        return series;
    }

    createSeriesByType(seriesType: any, measure: any, numberFormat: any, bigPrefixes: any, settings: any, valueFieldName: any) {
        let series = !seriesType || seriesType == 'bar' ? this.chart.series.push(new am4charts.ColumnSeries()) : this.chart.series.push(new am4charts.LineSeries());
        series.name = this.getSeriesName(measure);
        series.numberFormatter = new am4core.NumberFormatter();
        series.numberFormatter.numberFormat = numberFormat;
        series.numberFormatter.bigNumberPrefixes = bigPrefixes;

        return series;
    }

    private setSeriesColumnProperties(series: any, settings: any, category: any, valueFormat: any, measureAppearance: any, context: any, numberFormat: any, data: any, additional: any) {
        let prefix = this.getPrefix(context, measureAppearance, settings);
        let suffix = this.getSuffix(context, measureAppearance, settings);
        let template = series.columns.template;
        const isStacked = settings?.stacked ?? false;
        const useCornerRadius = !isStacked;

        template.fillOpacity = .8;
        template.strokeWidth = 1;
        template.strokeOpacity = 0.8;
        template.percentWidth = settings.spanPercent == null ? 80 : settings.spanPercent;
        template.maxWidth = settings.maxSpanPixels == null ? undefined : settings.maxSpanPixels;

        this.setCornerRadius(useCornerRadius, template.column);
        //if (useCornerRadius) {
        //    template.column.adapter.add("cornerRadiusTopLeft", (radius: any, target: any) => (target.dataItem && (target.dataItem.valueY < 0)) ? 0 : 4);
        //    template.column.adapter.add("cornerRadiusTopRight", (radius: any, target: any) => (target.dataItem && (target.dataItem.valueY < 0)) ? 0 : 4);
        //    template.column.adapter.add("cornerRadiusBottomLeft", (radius: any, target: any) => (target.dataItem && (target.dataItem.valueY > 0)) ? 0 : 4);
        //    template.column.adapter.add("cornerRadiusBottomRight", (radius: any, target: any) => (target.dataItem && (target.dataItem.valueY > 0)) ? 0 : 4);

        //    //template.column.cornerRadius(4, 4, 0, 0);
        //}

        series.stacked = isStacked;
        const showLegendName = this.shouldPrefixLegendNameInTooltip(context);
        let tooltipText = showLegendName
            ? `{name} - ${this.dateCategoryFormatter(category)}: [bold]${prefix}${valueFormat}${suffix}[/]`
            : settings.isCluster
                ? `${this.dateCategoryFormatter(category)} - {name}: [bold]${prefix}${valueFormat}${suffix}[/]`
                : `${this.dateCategoryFormatter(category)}: [bold]${prefix}${valueFormat}${suffix}[/]`;

        if (settings.stacked && settings.percentage && settings.showMultiTooltips)
            series.tooltipText = tooltipText;
        else
            template.tooltipText = tooltipText;

        if (!context.settings.showMultiTooltips)
            series.cursorTooltipEnabled = false;

        this.setBulletsVisibility(data, measureAppearance, additional);
        this.appendColumnActiveState(series);
        this.onClickColumnHandler(this.chart, series, context);
    }

    setSeriesLineProperties(series: any, settings: any, category: any, valueFormat: any, measureAppearance: any, context: any, data: any, additional: any) {
        let prefix = this.getPrefix(context, measureAppearance, settings);
        let suffix = this.getSuffix(context, measureAppearance, settings);
        let template = series;
        template.minBulletDistance = 0;
        template.strokeWidth = 3;
        template.strokeOpacity = 0.8;

        if (measureAppearance)
            series.fillOpacity = measureAppearance.fillArea ? 0.3 : undefined;

        const namePrefix = this.shouldPrefixLegendNameInTooltip(context) ? "{name} - " : "";
        let tooltipText = `${namePrefix}${this.dateCategoryFormatter(category)}: [bold]${prefix}${valueFormat}${suffix}[/]`;

        //let bullet = series.bullets.push(new am4charts.CircleBullet());
        //bullet.circle.strokeWidth = 2;
        //bullet.circle.radius = 4;
        //bullet.circle.fill = am4core.color("#fff");
        //bullet.tooltipText = tooltipText;
        //let bullethover = bullet.states.create("hover");
        //bullethover.properties.scale = 1.3;

        if (settings.stacked && settings.percentage && settings.showMultiTooltips)
            series.tooltipText = tooltipText;
        else
            template.tooltipText = tooltipText;

        template.tooltip.pointerOrientation = "vertical";
        template.tooltip.label.textAlign = "middle";
        template.tooltip.label.textValign = "middle";
        this.setBulletsVisibility(data, measureAppearance, additional);
    }

    setSeriesSettings(series: any, settings: any, valueFormat: any, context: any, measureAppearance: any = null, measureIndex: number | null = null) {
        if (settings.stacked && settings.percentage) {
            series.calculatePercent = true;
            series.legendSettings.itemValueText = settings.showLegendPercent ? valueFormat : "";
            series.dataFields.valueYShow = "totalPercent";
        }

        let firstDimension = context.settings?.dimensions == undefined ? null : settings?.dimensions[0];
        const useMeasureGradient = measureAppearance?.useGradient === true;
        const useDimensionGradient = !measureAppearance?.theme && measureIndex === 0 && firstDimension?.useGradient;

        if (useMeasureGradient || useDimensionGradient)
            series.columns.template.adapter.add("fill", (fill: any, target: any) => target.dataItem
                ? this.getThemeColor(context, measureAppearance, measureIndex, target.dataItem.index) ?? fill
                : fill);
    }

    updateSeries(context: any, valueFieldName?: any) {
        let settings = context.settings;
        let valueSeries = this.chart.series._values.find((s: any) => s?.dataFields?.valueY === valueFieldName);

        if (settings.measures != null) {
            let measureAppearance = this.context.isCluster ? settings.measures[0] : settings.measures.find((f: any) => f.name === valueFieldName);
            this.setBullets(valueSeries, settings, measureAppearance, valueFieldName);
        }

        Amcharts.prototype.updateSeries.call(this, context);
    }

    setBulletsVisibility(data: any, measureAppearance: any, additional: any = null) {
        const { definedFieldName, categoryFieldName, keyDimensionName, seriesTitle, dimensionAppearance } = additional;
        if (data) {
            let disablePropName = "_disabled_" + definedFieldName;
            let dateCompared = dimensionAppearance?.threshold?.selectedDate ? new Date(dimensionAppearance.threshold.selectedDate) : new Date();
            for (let key in data) {
                let value = data[key];
                if (!seriesTitle || value[keyDimensionName] === seriesTitle) {
                    let itemDate = new Date(value[categoryFieldName]);
                    const maxDate = new Date(Math.max(...data.map((item: any) => new Date(item[categoryFieldName]))));
                    if (measureAppearance?.bulletsType === "last")
                        value[disablePropName] = itemDate < maxDate;
                    else if (this.isDimensionalThreshold(dimensionAppearance))
                        value[disablePropName] = itemDate < dateCompared
                    else if (this.isMeasureCustomThreshold(measureAppearance))
                        value[disablePropName] = value[measureAppearance.name] > measureAppearance?.thresholdCustomValue
                    else if (this.isMeasureNegativeThreshold(measureAppearance))
                        value[disablePropName] = value[measureAppearance.name] > 0
                }
            }
        }
    }

    setBullets(series: any, settings: any, measureAppearance: any, valueFieldName: any) {
        let circleBullet = series.bullets.push(new am4charts.CircleBullet());
        circleBullet.circle.strokeWidth = 2;
        circleBullet.circle.radius = 3;
        circleBullet.circle.fill = am4core.color(this.getBulletColor(measureAppearance, series));

        switch (measureAppearance?.bulletSize) {
            case "small":
                circleBullet.circle.radius = 2;
                break;
            case "medium":
                circleBullet.circle.radius = 3;
                break;
            case "large":
                circleBullet.circle.radius = 6;
                break;
            case "none":
            default:
                circleBullet.circle.radius = 0;
                break;
        }

        let bullethover = circleBullet.states.create("hover");
        bullethover.properties.scale = 1.3;

        let prefix = this.getPrefix(this.context, measureAppearance, settings);
        let suffix = this.getSuffix(this.context, measureAppearance, settings);
        let labelBullet = series.bullets.push(new am4charts.LabelBullet());
        let numberFormat = this.getMeasureNumberFormat(measureAppearance, settings);

        if (this.isNeedToShowLabels(this.showLabelsFromApp, this.context.showLabels) || this.context.showLabels) {
            labelBullet.label.text = `${prefix}{valueY.formatNumber('${numberFormat}')}${suffix}`;
            labelBullet.label.adapter.add("text", (text: any, target: any) => {
                if (!(settings.stacked && settings.percentage)) return text;

                const dataItem = target.dataItem as any;
                const percent = dataItem?.values?.valueY?.totalPercent;

                const rounded = Math.max(1, Math.round(percent));
                return percent == null ? "" : `${this.chart.numberFormatter.format(rounded, "#")}%`;
            });

            switch (measureAppearance?.bulletsType) {
                case "all":
                    labelBullet.disabled = false;
                    break;
                case "threshold":
                case "last":
                    labelBullet.disabled = true;
                    labelBullet.propertyFields.disabled = `_disabled_${valueFieldName}`;
                    break;
                default:
                    labelBullet.disabled = true;
                    delete labelBullet.propertyFields.disabled;
                    break;
            }

            //bulletsSize was previous name for labelSize.
            switch (measureAppearance?.bulletsSize) {
                case "small":
                    labelBullet.fontSize = 10;
                    break;
                case "medium":
                    labelBullet.fontSize = 13;
                    break;
                case "large":
                    labelBullet.fontSize = 16;
                    break;
            }

            labelBullet.label.fontWeight = 'bold';
            labelBullet.label.hideOversized = false;
            labelBullet.label.truncate = false;
            labelBullet.label.fill = am4core.color(measureAppearance?.labelColor ?? this.getDefaultLabelColor());

            this.addBulletAdapters(labelBullet, settings, measureAppearance, circleBullet.circle.radius);
        }
    }

    addBulletAdapters(labelBullet: any, settings: any, measureAppearance: any, bulletRadius: number = 3) {
        const position = measureAppearance?.labelBulletPosition ?? settings.labelBulletPosition;
        const distance = measureAppearance?.labelDistance ?? "medium";
        const showLines = measureAppearance?.labelShowLines ?? false;

        const isInside = position === 'inside';
        const offsetValue = this.getLabelDistanceOffset(distance);

        const lineLength = showLines && !isInside ? offsetValue : 0;
        const totalOffset = isInside ? offsetValue : offsetValue + lineLength + 5;
        const isHorizontal = false;

        this.createLabelLine(labelBullet, measureAppearance, showLines, isInside);
        this.addLabelPositionAdapter(labelBullet.label, isInside, isHorizontal);
        this.addLabelDyAdapter(labelBullet.label, isInside, offsetValue, totalOffset, 'valueY');

        if (showLines && !isInside) {
            this.addLineY1Adapter(labelBullet.line, bulletRadius, isHorizontal);
            this.addLineY2Adapter(labelBullet.line, bulletRadius, totalOffset, isHorizontal);
            this.addLineX1Adapter(labelBullet.line, bulletRadius, isHorizontal);
            this.addLineX2Adapter(labelBullet.line, bulletRadius, totalOffset, isHorizontal);
        }

        this.setFixedBigNumberLabel(labelBullet.label, measureAppearance, settings);
    }

    getSeriesType(measureName: any, settings: any) {
        if (settings && settings.measures) {
            let measureAppearance = this.context.isCluster ? settings.measures.find((m: any) => this.context.basicMeasures.find((x: any) => x.resultName == m.name)) : settings.measures.find((m: any) => measureName.includes(m.name));

            if (measureAppearance)
                return measureAppearance.seriesType;
        }
    }

    updateSettings(context: any) {
        super.updateSettings(context);

        if (context.settings) {
            this.updateCategoryAxis(context);
            this.updateValueAxes(context);
            this.removeSerieses();
            this.createSerieses(context);
            this.updateDimensions(context);
            this.updateMeasures(context);

            if (this.chart.data && this.chart.data.length > 0)
                this.setLegend(context);

            //this._applyScrollbar(context.settings.labelMaxWidth);
        }
    }

    updateCategoryAxisSettings(categoryAxis: any, context: any) {
        let isCluster = context.isCluster;
        categoryAxis.renderer.cellStartLocation = isCluster ? context.cellStartLocation : 0;
        categoryAxis.renderer.cellEndLocation = isCluster ? context.cellEndLocation : 1;

        let dimension = context.settings?.dimensions?.length ? context.settings.dimensions?.filter((d: any) => d.name != null && d.categoryLabelRotation)[0] : {};
        categoryAxis.renderer.minGridDistance = dimension?.categoryLabelDistance || 30;

        let label = categoryAxis.renderer.labels.template;
        label.rotation = dimension?.categoryLabelRotation;
        label.html = null;
        label.wrap = false;
        label.truncate = false;
        label.maxWidth = context.settings.labelMaxWidth;
        if (label.maxWidth) {
            label.truncate = context.settings.labelTruncateText;
            label.wrap = !label.truncate;

            if (label.truncate) {
                label.tooltipText = label.truncate ? `${this.dateCategoryFormatter(`category`)}` : null;
                // Force the tooltip bubble to always display on the right
                label.tooltip = new am4core.Tooltip();
                label.tooltip.pointerOrientation = "right";
                // Only show tooltip if it is oversized
                label.adapter.add("tooltipText", function (text: string, target: any) {
                    return target && !target.isOversized ? null : text;
                });
            }
        }
        if (label.rotation)
            label.verticalCenter = "middle";
        
        label.horizontalCenter = "middle";

        //label.tooltipText = `${this.dateCategoryFormatter(`category`)}`;
        //const labelMaxWidth = context.settings.labelMaxWidth;
        //label.html = "";
        //label.maxWidth = labelMaxWidth;// || 130;
        //label.truncate = false;
        //label.wrap = true;
        //let labelCenter = this.getLabelCenter(label.rotation);
        //label.horizontalCenter = labelCenter.horizontalCenter;
        //label.verticalCenter = labelCenter.verticalCenter;

        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.title.fontWeight = "bold";

        categoryAxis.numberFormatter = new am4core.NumberFormatter();
        categoryAxis.numberFormatter.numberFormat = this.getDimensionNumberFormat(dimension);
    }

    updateCategoryAxis(context: any) {
        if (context.settings == null) return;

        let categoryAxis = this.chart.xAxes.getIndex(0);
        if (categoryAxis)
            this.updateCategoryAxisSettings(categoryAxis, context);
    }

    removeCategoryAxis(index: any) {
        this.chart.xAxes.removeIndex(index);
    }

    updateValueAxes(context: any) {
        for (let i = 0; i < context.measures.length; i++) {
            let measure = context.measures[i];
            let measureResultName = measure.resultName || "value" + i;
            let measureAppearance = context.settings.measures?.length
                ? context.settings.measures?.find((m: any) => m.name === measure.resultName)
                : {};
            let isOpposite = measureAppearance?.hasOwnAxis ? measureAppearance.axisPosition === "opposite" : false;

            let yAxis = this.chart.yAxes?.values?.find((axis: any) => axis.dataFields.resultName === measureResultName);

            if (yAxis !== undefined && yAxis !== null)
                yAxis.renderer.opposite = isOpposite;

            this.updateValueAxis(context, yAxis, measure);
        }

        this.baseSyncAxesByZero(context, this.chart.yAxes);
    }

    updateValueAxis(context: any, valueAxis: any, measure: any) {
        if (context?.settings == null) return;

        let firstAxis = this.chart.yAxes?.values?.[0];
        let measureAppearance = context.settings.measures?.find((m: any) => m.name === measure.resultName);

        if (valueAxis) {
            valueAxis.title.fontWeight = "bold";
            valueAxis.title.text = this.translateFromMeasure(context, measureAppearance, "axisTitle");
            valueAxis.numberFormatter = new am4core.NumberFormatter();
            valueAxis.max = context.settings.axisMaxValue;
            valueAxis.min = context.settings.axisMinValue;
            if (context.settings.axisGridDistance != null)
                valueAxis.renderer.minGridDistance = context.settings.axisGridDistance;

            if (context.settings?.stacked && context.settings?.percentage)
                valueAxis = this.setPercentageAxis(valueAxis);
            else {
                valueAxis.numberFormatter.bigNumberPrefixes = this.getBigNumberPrefixes(measureAppearance, context.settings);
                valueAxis.numberFormatter.numberFormat = this.getMeasureNumberFormat(measureAppearance, context.settings);
            }
        }
        if (valueAxis && valueAxis === firstAxis)
            valueAxis.title.text = this.translateFromMeasure(context, measureAppearance, "axisTitle") || this.translateFromSettings(context, "valueAxisTitle");
    }

    updateDimensions(context: any) {
        let settings = context.settings || {};
        if (!settings?.dimensions) return;

        for (let dimension of settings.dimensions) {
            for (let j = 0; j < this.chart.xAxes.length; j++) {
                let xAxis = this.chart.xAxes.getIndex(j);
                if (xAxis.dataFields.category == dimension.name) {
                    xAxis.title.text = this.traslateFromDimension(context, dimension, "title");
                }
            }
        }
    }

    updateMeasures(context: any) {
        let settings = context.settings || {};
        if (!settings?.measures) return;

        const measureAppearances = settings.measures;
        if (measureAppearances.length) {
            for (const measureAppearance of measureAppearances) {
                for (let j = 0; j < this.chart.series.length; j++) {
                    const serie = this.chart.series.getIndex(j);
                    if (serie.dataFields.valueY.includes(measureAppearance.name)) {
                        let appearanceTitle = this.translateFromMeasure(context, measureAppearance, "title");
                        let metaFieldTitle = context.measureMetaDataFields.find((f: any) => f.resultName == measureAppearance.name)?.title;
                        serie.name = !appearanceTitle ? metaFieldTitle : appearanceTitle;
                    }
                }
            }
        }
    }

    setData(context: any) {
        if (context.data) {
            this.chart.colors.reset();
            this.chart.data = context.data;
            if (context.data.length) {

                this.setLegend(context);
                this.updateValueAxes(context);
                this.syncValueAxes(context);
            }
        }
        else
            this.clearData();
    }

    syncValueAxes(context: any) {
        const values = this.chart.yAxes.values;
        if (values?.length > 0) {
            const mainAxis = this.getMainValueAxis(context);
            this.baseSyncValueAxes(this.chart.yAxes, mainAxis);
        }
    }

    getMainValueAxis(context: any): any {
        const measureAppearancesWithOwnAxes = context?.settings?.measures?.filter((m: any) => m.hasOwnAxis);
        const measureAppearancesWithDeltaAxes = context?.measures?.filter((m: any) => m?.valueKind != 0);

        const yAxesWithMultipleGeneral = this.getMarkedMultipleAxes(this.chart.yAxes, measureAppearancesWithOwnAxes,
            { measuresSettingsWithDeltaAxes: measureAppearancesWithDeltaAxes, mode: "general" });

        const mainAxis = yAxesWithMultipleGeneral.find((axis: any) => (axis !== null && axis !== undefined));
        return mainAxis ?? null;
    }

    markActiveByValue(values: any[]) {
        setTimeout(() => {
            this.chart.series.each((s: any) => s.columns.each((c: any, _i: number) => {
                values.forEach((v: any) => {
                    if (v == c.dataItem.categoryX)
                        c.isActive = true;
                })
            }));
        }, 100);
    }

    getAppropriateYAxis(valueFieldName: any, context: any, hasOwnAxis: boolean, isDeltaAxis: boolean) {
        if (hasOwnAxis || isDeltaAxis)
            return this.getActiveAxis(this.chart.yAxes, valueFieldName);
        else {
            const measureAppearancesWithOwnAxes = context?.settings?.measures?.filter((m: any) => m.hasOwnAxis);
            const measureAppearancesWithDeltaAxes = context?.measures?.filter((m: any) => m?.valueKind != 0);

            const yAxesMarkedMultipleSpecial = this.getMarkedMultipleAxes(this.chart.yAxes, measureAppearancesWithOwnAxes,
                { measuresSettingsWithDeltaAxes: measureAppearancesWithDeltaAxes, mode: "special" });

            const appropriateAxis = yAxesMarkedMultipleSpecial.find((axis: any) => (axis !== null && axis !== undefined));
            return appropriateAxis ?? this.chart.yAxes.values[0];
        }
    }

    removeValueAxisByResultName(resultName: string) {
        let yAxis = this.chart.yAxes?.values?.find((axis: any) => axis.dataFields.resultName === resultName);
        if (yAxis !== undefined) {
            let index = this.chart.yAxes.indexOf(yAxis);
            this.chart.yAxes.removeIndex(index);
        }
    }

    private _applyScrollbar(labelMaxWidth: number | null | undefined): void {
        if (labelMaxWidth) {
            if (!this.chart.scrollbarX)
                this.chart.scrollbarX = new am4core.Scrollbar();
            this.chart.scrollbarX.parent = this.chart.topAxesContainer;
        } else {
            this.chart.scrollbarX = null as any;
        }
    }
}